import type { Placement } from '@popperjs/core';
import classNames from 'classnames';
import type { MouseEventHandler, ReactNode } from 'react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type {
  Modifier,
  PopperChildrenProps,
  ReferenceChildrenProps,
} from 'react-popper';
import { Manager, Popper, Reference } from 'react-popper';
import * as styles from './PopOver.module.scss';
import closeIcon from '../../icons/non-prefilled/16x16/clear.svg';
import ClickOutsideHandler from '../../utils/ClickOutsideHandler';
import isSize from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';
import ErrorBoundary from '../ErrorBoundary';
import IconButton from '../IconButton';

type TRIGGER_TYPE = 'click' | null;

interface PopOverProps {
  children: ReactNode | ((props: ReferenceChildrenProps) => ReactNode);
  placement: Placement;
  arrow?: JSX.Element | null;
  trigger?: TRIGGER_TYPE;
  responsivePlacement?: 'top' | 'bottom' | 'full';
  preventCloseOnContentClick?: boolean;
  popOverPortal?: HTMLElement | null;
  popOver?: ReactNode | ((props: PopperChildrenProps) => ReactNode);
  display?: 'block' | 'inline-block' | 'inline';
  disablePersist?: boolean;
  modifiers?: Modifier<unknown, Record<string, unknown>>[];
}

interface ControlledPopOverProps extends PopOverProps {
  open: boolean;
  onOpenChange: (newOpen: boolean) => void;
}

interface UncontrolledPopOverProps extends PopOverProps {
  open?: never;
  onOpenChange?: never;
}

const ControlledPopOver = ({
  arrow = null,
  children,
  disablePersist = false,
  display = 'block',
  modifiers,
  onOpenChange,
  open,
  placement,
  popOver = null,
  popOverPortal = null,
  preventCloseOnContentClick = false,
  responsivePlacement,
  trigger: preferredTrigger = null,
}: ControlledPopOverProps) => {
  const updateRef = useRef<(() => unknown) | null>(null);
  const [popOverInner, setPopOverInner] = useState<HTMLDivElement | null>(null);
  const documentMouseOverRef = useRef<((event: MouseEvent) => void) | null>(
    null,
  );

  const trigger =
    preferredTrigger && (isSize(Sizes.SM) ? 'click' : preferredTrigger);

  const [hasBeenOpened, setHasBeenOpened] = useState(open);

  const handleClick = () => onOpenChange(!open);

  const handleClickOutside = () => {
    if (trigger && open) {
      onOpenChange(false);
    }
  };

  const handleInsideDismissClick: MouseEventHandler = (e) => {
    onOpenChange(false);
    e.stopPropagation(); // prevent handleClick gets called
  };

  const handleContentClick: MouseEventHandler = (e) => {
    if (preventCloseOnContentClick) {
      e.stopPropagation();
      return;
    }
    handleClick();
  };

  const triggerHandlers = trigger ? { onClick: handleClick } : {};

  useEffect(() => {
    if (open) {
      setHasBeenOpened(true);
      updateRef.current?.();
    }
  }, [open]);

  useEffect(() => {
    // when the content within the same pop over changes, popper does not
    // get any update callbacks, so we need to monitor DOM changes and reposition
    // popper
    if (typeof MutationObserver === 'undefined' || !open || !popOverInner) {
      return undefined;
    }

    const observer = new MutationObserver(() => {
      if (updateRef.current) {
        updateRef.current();
      }
    });

    observer.observe(popOverInner, {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [open, popOverInner]);

  useEffect(
    () => () => {
      if (documentMouseOverRef.current) {
        document.removeEventListener('mouseover', documentMouseOverRef.current);
      }
    },
    [],
  );

  const finalModifiers = useMemo<Modifier<unknown, Record<string, unknown>>[]>(
    () => [
      {
        name: 'arrow',
        options: { padding: 6 },
      },
      {
        name: 'preventOverflow',
        options: { padding: 12 },
      },
      {
        name: 'resizeModifier',
        enabled: true,
        phase: 'main',
        fn: () => {},
        effect: ({ state, instance }) => {
          const resizeObserver = new ResizeObserver(() => {
            instance.update();
          });
          resizeObserver.observe(state.elements.popper);
          return () => resizeObserver.disconnect();
        },
      },
      ...(modifiers ?? []),
    ],
    [modifiers],
  );

  const popper = (
    <ErrorBoundary name="popOver:popper">
      <Popper placement={placement} modifiers={finalModifiers}>
        {(popperProps) => {
          const { ref, update, style } = popperProps;
          const activePlacement = popperProps.placement || placement;
          updateRef.current = update;

          return (
            <>
              {open && responsivePlacement && (
                <div
                  data-no-print
                  className={styles.popOverResponsiveBackdrop}
                  onClick={trigger ? handleInsideDismissClick : undefined}
                />
              )}
              <div
                className={classNames(styles.popOverPopperPopperOuter, {
                  // Cannot be on `pop-over` since popper could be rendered through portal
                  [styles.hidden]: !open,
                  ...(responsivePlacement
                    ? {
                        [styles[responsivePlacement]]: responsivePlacement,
                      }
                    : {}),
                })}
                data-placement={activePlacement}
                style={style}
                onClick={trigger ? handleContentClick : undefined}
                ref={ref}
              >
                <div
                  className="popper-inner"
                  ref={(element) => {
                    if (popOverInner !== element) {
                      setPopOverInner(element);
                    }
                  }}
                >
                  {typeof popOver === 'function'
                    ? popOver({
                        ...popperProps,
                        placement: activePlacement,
                      })
                    : popOver}
                </div>
                {!!arrow &&
                  React.cloneElement(arrow, {
                    'data-popper-arrow': true,
                    ...popperProps.arrowProps,
                  })}
              </div>
            </>
          );
        }}
      </Popper>
    </ErrorBoundary>
  );

  return (
    <ClickOutsideHandler
      onClickOutside={handleClickOutside}
      className={classNames(styles.popOver, 'pop-over', {
        [styles.popOverInline]: display === 'inline',
        [styles.popOverInlineBlock]: display === 'inline-block',
      })}
      data-testid="pop-over"
      {...triggerHandlers}
    >
      <Manager>
        <Reference>
          {(referenceProps) =>
            typeof children === 'function' ? (
              children(referenceProps)
            ) : (
              <div
                className={styles.popOverPopperTarget}
                ref={referenceProps.ref}
              >
                {children}
              </div>
            )
          }
        </Reference>
        {hasBeenOpened &&
          (!disablePersist || open) &&
          !!popOver &&
          (popOverPortal ? createPortal(popper, popOverPortal) : popper)}
      </Manager>
    </ClickOutsideHandler>
  );
};

const UncontrolledPopOver = (props: UncontrolledPopOverProps) => {
  const [isOpen, setIsOpen] = useState(!props.trigger);
  return (
    <ControlledPopOver {...props} open={isOpen} onOpenChange={setIsOpen} />
  );
};

const PopOver = (props: ControlledPopOverProps | UncontrolledPopOverProps) => (
  <ErrorBoundary name="popOver:reference">
    {'onOpenChange' in props && props.onOpenChange ? (
      <ControlledPopOver {...props} />
    ) : (
      <UncontrolledPopOver {...props} />
    )}
  </ErrorBoundary>
);

interface PopOverContainerProps {
  close?: () => void;
  overflow?: 'visible' | 'auto';
  mobileTitle?: string;
  title?: string;
  width?: 'regular' | 'auto' | 'wide';
  fullHeight?: boolean;
  noMargin?: boolean;
  noPadding?: boolean;
  children:
    | React.ReactNode
    | ((args: { scrollToBottom: () => void }) => React.ReactNode);
}

const PopOverContainer = ({
  children,
  close,
  width = 'regular',
  overflow = 'auto',
  title,
  mobileTitle = title,
  fullHeight,
  noMargin,
  noPadding,
}: PopOverContainerProps) => {
  const scrollElementRef = useRef<HTMLDivElement>(null);

  const finalTitle = isSize(Sizes.SM) ? mobileTitle : title;

  const scrollToBottom = () => {
    if (scrollElementRef.current) {
      scrollElementRef.current.scrollTop =
        scrollElementRef.current.scrollHeight;
    }
  };

  const inner =
    typeof children === 'function'
      ? children({
          scrollToBottom,
        })
      : children;

  return (
    <div
      className={classNames(styles.popOverContainer, {
        [styles.widthAuto]: width === 'auto',
        [styles.widthWide]: width === 'wide',
        [styles.showMargin]: !noMargin,
        [styles.maxHeightFull]: fullHeight,
      })}
    >
      <div className={styles.popOverContentWrapper}>
        {finalTitle && (
          <div className={styles.popOverContainerHeader}>
            <div className={styles.popOverContainerTitle}>{finalTitle}</div>
            <IconButton
              className={styles.popOverContainerClose}
              onClick={close}
              applyNegativeMargin
              aria-label="Close"
            >
              {closeIcon}
            </IconButton>
          </div>
        )}
        <div
          className={classNames(styles.popOverContainerContent, {
            [styles.overflowVisible]: overflow === 'visible',
          })}
          ref={scrollElementRef}
        >
          {noPadding ? (
            inner
          ) : (
            <div className={styles.popOverContainerContentInner}>{inner}</div>
          )}
        </div>
      </div>
    </div>
  );
};

interface PopOverContainerArrowProps
  extends React.HTMLAttributes<HTMLDivElement> {
  noMargin?: boolean;
  small?: boolean;
}

const PopOverContainerArrow = React.forwardRef<
  HTMLDivElement,
  PopOverContainerArrowProps
>(({ noMargin, small, ...props }, ref) => (
  <div
    ref={ref}
    className={classNames(styles.popOverContainerArrow, {
      [styles.showMargin]: !noMargin,
      [styles.popOverContainerArrowSmall]: small,
    })}
    {...props}
  />
));

PopOverContainer.Arrow = PopOverContainerArrow;

PopOver.Container = PopOverContainer;

export default PopOver;
